import React, { useRef, useEffect, useState } from 'react';

const ProductsSection = ({ title, products }) => {
  const snapScrollContainerRef = useRef(null);
  const [activeProductIndex, setActiveProductIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 767);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const container = snapScrollContainerRef.current;
    const productElements = container?.querySelectorAll('[data-snap-item]') || [];

    if (!container || productElements.length === 0) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const itemHeight = productElements[0].offsetHeight;
      const currentIndex = Math.round(scrollTop / itemHeight);
      setActiveProductIndex(currentIndex);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll logic (disabled on mobile for smoother UX)
  useEffect(() => {
    if (isMobile) return; // ✅ prevent auto-scroll trap on mobile

    const container = snapScrollContainerRef.current;
    const productElements = container?.querySelectorAll('[data-snap-item]') || [];

    if (!container || productElements.length === 0) return;

    const interval = setInterval(() => {
      if (isPaused) return;

      setActiveProductIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % productElements.length;
        const itemHeight = productElements[0].offsetHeight;
        container.scrollTo({
          top: nextIndex * itemHeight,
          behavior: "smooth",
        });
        return nextIndex;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused, products.length, isMobile]);

  useEffect(() => {
    const container = snapScrollContainerRef.current;

    const handleMouseEnter = () => setIsPaused(true);
    const handleMouseLeave = () => setIsPaused(false);

    if (container) {
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, []);

  // ✅ Allow scroll to escape when at top/bottom
  useEffect(() => {
    const container = snapScrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (
        (e.deltaY < 0 && container.scrollTop === 0) || // scrolling up at top
        (e.deltaY > 0 &&
          container.scrollTop + container.clientHeight >= container.scrollHeight) // scrolling down at bottom
      ) {
        // Let the scroll propagate to the main page
        e.preventDefault();
        window.scrollBy(0, e.deltaY);
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, []);

  const handleArrowClick = (direction) => {
    const container = snapScrollContainerRef.current;
    const itemHeight = container?.querySelector('[data-snap-item]')?.getBoundingClientRect().height || 1;

    let newIndex =
      direction === "left"
        ? Math.max(0, activeProductIndex - 1)
        : Math.min(products.length - 1, activeProductIndex + 1);

    container?.scrollTo({
      top: itemHeight * newIndex,
      behavior: "smooth",
    });

    setActiveProductIndex(newIndex);
  };

  const sectionStyle = {
    maxWidth: '1280px',
    margin: '0 auto'
  };

  const titleStyle = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#111827'
  };

  const containerStyle = {
    position: 'relative'
  };

  const scrollContainerStyle = {
    overflowY: 'auto',
    scrollSnapType: 'y mandatory',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none'
  };

  const snapItemStyle = {
    height: '350px',
    scrollSnapAlign: 'start',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const snapItemMobileStyle = {
    height: '100%',
    scrollSnapAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    textAlign: 'center'
  };

  const contentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '64px',
    maxWidth: '1152px',
    width: '100%'
  };

  const contentMobileStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  };

  const imageContainerStyle = {
    flex: '0 0 500px',
    height: '300px'
  };

  const imageContainerMobileStyle = {
    width: '100%',
    height: '200px',
    overflow: 'hidden',
    borderRadius: '26px',
  };

  const imageStyle = {
    width: '100%',
    height: '270px',
    objectFit: 'cover',
    borderRadius: '26px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    transition: 'transform 0.3s ease'
  };

  const textContainerStyle = {
    flex: '1',
    maxWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginLeft: '60px'
  };

  const textContainerMobileStyle = {
    flex: 1,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center'
  };

  const productNameStyle = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '5px'
  };

  const productNameMobileStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '0',
    marginTop: '0'
  };

  const featuresListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const featureItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    color: '#374151',
    fontSize: '1.125rem'
  };

  const featureItemMobileStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    color: '#374151',
    fontSize: '1rem',
    textAlign: 'left'
  };

  const bulletStyle = {
    width: '8px',
    height: '8px',
    backgroundColor: '#3B82F6',
    borderRadius: '50%',
    marginTop: '12px',
    flexShrink: 0
  };

  const bulletMobileStyle = {
    width: '6px',
    height: '6px',
    backgroundColor: '#3B82F6',
    borderRadius: '50%',
    marginTop: '8px',
    flexShrink: 0
  };

  const learnMoreStyle = {
    overflow: 'hidden',
    paddingTop: '16px'
  };

  const learnMoreLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'lightgreen',
    fontWeight: '600',
    fontSize: '1.125rem',
    textDecoration: 'none',
    transition: 'color 0.2s ease'
  };

  const learnMoreLinkMobileStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'lightgreen',
    fontWeight: '600',
    fontSize: '1rem',
    textDecoration: 'none',
    transition: 'color 0.2s ease'
  };

  const arrowControlsStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '32px'
  };

  const arrowButtonStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'white',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B7280',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '18px'
  };

  const arrowButtonDisabledStyle = {
    ...arrowButtonStyle,
    opacity: 0.5,
    cursor: 'not-allowed'
  };

  return (
    <section style={sectionStyle}>
      <h2 style={titleStyle}>{title}</h2>

      <div style={containerStyle}>
        <div
          ref={snapScrollContainerRef}
          style={{
            ...scrollContainerStyle,
            maxHeight: isMobile ? '550px' : '350px', // ✅ maxHeight instead of fixed
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {products.map((product, index) => (
            <div
              key={index}
              data-snap-item
              style={
                isMobile
                  ? { ...snapItemMobileStyle, height: '500px' }
                  : { ...snapItemStyle, height: '350px' }
              }
            >
              <div style={isMobile ? contentMobileStyle : contentStyle}>
                {/* Product Image */}
                <div
                  style={
                    isMobile ? imageContainerMobileStyle : imageContainerStyle
                  }
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    style={imageStyle}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        e.target.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  />
                </div>

                {/* Product Content */}
                <div
                  style={isMobile ? textContainerMobileStyle : textContainerStyle}
                >
                  {isMobile ? (
                    <a
                      href={product.learnMoreLink}
                      style={{
                        ...learnMoreLinkMobileStyle,
                        textDecoration: 'none',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#2563EB';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#111827';
                      }}
                    >
                      <h3 style={productNameMobileStyle}>{product.name}</h3>
                    </a>
                  ) : (
                    <h3 style={productNameStyle}>{product.name}</h3>
                  )}

                  <ul style={featuresListStyle}>
                    {product.features.map((feature, idx) => (
                      <li
                        key={idx}
                        style={isMobile ? featureItemMobileStyle : featureItemStyle}
                      >
                        <span
                          style={isMobile ? bulletMobileStyle : bulletStyle}
                        ></span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={learnMoreStyle}>
                    <a
                      href={product.learnMoreLink}
                      style={
                        isMobile ? learnMoreLinkMobileStyle : learnMoreLinkStyle
                      }
                      onMouseEnter={(e) => {
                        e.target.style.color = '#1D4ED8';
                        e.target.querySelector('span').style.transform =
                          'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#2563EB';
                        e.target.querySelector('span').style.transform =
                          'translateX(0)';
                      }}
                    >
                      Learn More
                      <span
                        style={{
                          transition: 'transform 0.2s ease'
                        }}
                      >
                        →
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <div style={arrowControlsStyle}>
          <button
            onClick={() => handleArrowClick('left')}
            disabled={activeProductIndex === 0}
            style={
              activeProductIndex === 0
                ? arrowButtonDisabledStyle
                : arrowButtonStyle
            }
          >
            ←
          </button>
          <button
            onClick={() => handleArrowClick('right')}
            disabled={activeProductIndex === products.length - 1}
            style={
              activeProductIndex === products.length - 1
                ? arrowButtonDisabledStyle
                : arrowButtonStyle
            }
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
